import { getPaymentConfig } from "../../utils/paymentConfig";
import { apiChargeForte } from "../../services/paymentService";
import axios from "axios";

/**
 * ForteProcessor: Handles Forte payment gateway integration
 * - Uses ForteJs library for client-side tokenization
 * - Submits charges to backend with one-time tokens
 */
class ForteProcessor {
  constructor() {
    this.forteJs = null;
    this.paymentConfig = null;
    this.forteApiLoginId = null;
    this.isInitialized = false;
  }

  /**
   * Initialize Forte payment processor
   * @param {Object} options - Initialization options
   * @param {string} options.domain - Domain for payment config
   * @param {string} options.merchant - Merchant type (forte)
   * @returns {Promise<boolean>} - Success status
   */
  async initialize({ domain, merchant }) {
    try {
      console.log('[ForteProcessor] initialize start', { domain, merchant });
      
      // Get Forte configuration mapping
      this.paymentConfig = getPaymentConfig(domain, merchant);
      console.log('[ForteProcessor] paymentConfig', this.paymentConfig);
      
      // Fetch Forte api_login_id from backend
      const response = await axios.post(
        `${process.env.REACT_APP_API || 'https://dev.beclutch.club/api'}/camp/get_public_payment_config`,
        { domain }
      );

      console.log('[ForteProcessor] payment config response', response?.data);
      
      if (!response.data.success) {
        throw new Error('Failed to load payment configuration from server');
      }

      if (!response.data.forte_api_login_id) {
        throw new Error('Forte api_login_id not found in server response. Please configure it in the database.');
      }

      this.forteApiLoginId = response.data.forte_api_login_id;
      console.log('[ForteProcessor] Forte api_login_id loaded:', this.forteApiLoginId);
      
      // Get ForteJs from window (loaded via script tag)
      this.forteJs = window.forte;
      
      if (!this.forteJs) {
        throw new Error('ForteJs library not loaded');
      }

      this.isInitialized = true;
      console.log('[ForteProcessor] initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize Forte:', error);
      throw error;
    }
  }

  /**
   * Create token via Forte
   * @param {Object} tokenData - Payment data for tokenization
   * @returns {Promise<string>} - One-time token
   */
  async createToken(tokenData) {
    if (!this.isInitialized || !this.forteJs) {
      throw new Error('Forte processor not initialized');
    }

    console.log('[ForteProcessor] createToken called');
    console.log('[ForteProcessor] tokenData:', JSON.stringify(tokenData, null, 2));
    console.log('[ForteProcessor] paymentConfig:', this.paymentConfig);

    return new Promise((resolve, reject) => {
      this.forteJs
        .createToken(tokenData)
        .success((res) => {
          console.log('[ForteProcessor] createToken SUCCESS:', res);
          if (res?.event === "success") {
            console.log('[ForteProcessor] Token created:', res.onetime_token);
            resolve(res.onetime_token);
          } else {
            console.error('[ForteProcessor] Tokenization failed (no success event):', res);
            reject(new Error(res?.error || "Tokenization failed"));
          }
        })
        .error((err) => {
          console.error('[ForteProcessor] createToken ERROR:', err);
          console.error('[ForteProcessor] Error details:', JSON.stringify(err, null, 2));
          reject(new Error(err?.message || "Token API error"));
        });
    });
  }

  /**
   * Process payment via Forte (tokenization + charge)
   * @param {Object} paymentData - Payment information
   * @returns {Promise<Object>} - Payment result with transaction details
   */
  async processPayment(paymentData) {
    if (!this.isInitialized || !this.forteJs) {
      throw new Error('Forte processor not initialized');
    }

    const {
      cardType,
      userData,
      registrationInfo,
      totalCheckout,
      processingFee,
      creditMin,
      // Card payment fields
      cardNumber,
      expiry,
      cvc,
      // eCheck fields
      routingNumber,
      accountNumber,
      accountType,
    } = paymentData;

    // Build tokenization data
    let tokenPayload = { 
      api_login_id: this.forteApiLoginId 
    };

    if (cardType === "echeck") {
      tokenPayload = {
        ...tokenPayload,
        account_number: accountNumber,
        routing_number: routingNumber,
        account_type: accountType,
      };
    } else {
      // Parse expiry: DatePicker returns "MM/YY" format (e.g., "01/25")
      const expiryParts = expiry.split('/').map(part => part.trim());
      if (expiryParts.length !== 2) {
        throw new Error('Invalid expiry date format. Expected MM/YY');
      }
      const expireMonth = expiryParts[0].padStart(2, '0');
      const expireYear = "20" + expiryParts[1];
      
      tokenPayload = {
        ...tokenPayload,
        card_number: cardNumber.replace(/\s/g, ""),
        expire_month: expireMonth,
        expire_year: expireYear,
        cvv: cvc,
      };
      
      console.log('[ForteProcessor] Parsed expiry:', { input: expiry, expireMonth, expireYear });
    }

    // Create token - log Forte script details
    console.log('[ForteProcessor] About to create token with payload:', tokenPayload);
    
    // Log what Forte scripts are loaded
    const forteScripts = Array.from(document.querySelectorAll('script[src*="forte"]'));
    console.log('[ForteProcessor] Forte scripts loaded from URLs:', forteScripts.map(s => s.src));
    
    // Log window.forte availability
    console.log('[ForteProcessor] window.forte available:', !!window.forte);
    if (window.forte) {
      console.log('[ForteProcessor] window.forte.createToken available:', typeof window.forte.createToken === 'function');
    } else {
      console.warn('[ForteProcessor] ⚠️ window.forte is NOT available! Forte script may not have loaded.');
    }
    
    try {
      const token = await this.createToken(tokenPayload);
      console.log('[ForteProcessor] Token created successfully');

      // Calculate service fee with minimum
      const serviceFeeAmount = parseFloat(processingFee) < parseFloat(creditMin) 
        ? parseFloat(creditMin) 
        : parseFloat(processingFee);

      console.log('[ForteProcessor] Service fee calculated:', serviceFeeAmount);

      // Submit charge to backend
      const payload = {
      domain: registrationInfo.domain,
      registration_id: registrationInfo.ID,
      merchant: "forte",
      authorization_amount: totalCheckout,
      service_fee_amount: serviceFeeAmount,
      billing_address: {
        first_name: userData.bill_first_name,
        last_name: userData.bill_last_name,
        address: userData.bill_address,
        city: userData.bill_city,
        state: userData.bill_state,
        postal_code: userData.bill_postal_code,
      },
      card_type: cardType === "echeck" ? "echeck" : "card",
      token,
    };

      console.log('[ForteProcessor] Submitting charge to backend:', payload);
      const chargeResponse = await apiChargeForte(payload);
      console.log('[ForteProcessor] Backend charge response:', chargeResponse);

      if (!chargeResponse?.result || !chargeResponse?.data) {
        console.error('[ForteProcessor] Backend charge failed:', chargeResponse);
        console.error('[ForteProcessor] Error message:', chargeResponse?.message);
        console.error('[ForteProcessor] Error data:', chargeResponse?.data);
        throw new Error(chargeResponse?.message || "Payment processing failed");
      }

      const transactionData = chargeResponse.data;

      return {
        success: true,
        transactionId: transactionData.transaction_id,
        customerId: transactionData.entered_by,
        authorizationCode: transactionData.authorization_code,
        locationId: transactionData.location_id,
        data: transactionData,
      };
    } catch (tokenError) {
      console.error('[ForteProcessor] Token creation failed:', tokenError);
      console.error('[ForteProcessor] Error response:', tokenError?.response);
      console.error('[ForteProcessor] Error data:', tokenError?.response?.data);
      console.error('[ForteProcessor] Stack trace:', tokenError.stack);
      throw tokenError;
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.forteJs = null;
    this.paymentConfig = null;
    this.forteApiLoginId = null;
    this.isInitialized = false;
  }
}

export default ForteProcessor;
